import { useEffect, useState, type RefObject } from 'react';

/**
 * Returns true when the referenced element's content overflows its visible box
 * horizontally (single-line ellipsis) or vertically (multi-line line-clamp).
 * Re-checks whenever the element is resized via ResizeObserver, or when
 * `content` changes.
 *
 * Pass `content` when the element's text can change without the element itself
 * resizing (e.g. a renamed label in a fixed-width container) — ResizeObserver
 * only fires on layout-box changes, not on scrollWidth/scrollHeight growth
 * from new text.
 */
export function useTruncated(ref: RefObject<HTMLElement | null>, content?: unknown): boolean {
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const check = () =>
      setIsTruncated(el.scrollWidth > el.offsetWidth || el.scrollHeight > el.clientHeight);
    check();

    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(check);
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref, content]);

  return isTruncated;
}
